import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import ReservationSidebar from "../../Commun/ReservationSidebar";
import ReservationHeader from "../../Commun/ReservationHeader";
import "./RoomGrid.css";
import { ResRoomlist } from "../../../Services/Service";
import FullScreenLoader from "../../../BON Frontend/Booking-Form/Common/FullScreenLoader";
import { useProperty } from "../../../PropertyContext";
import { saveBlockRoomGrid, resetBlockRoomGridState } from "../../../Redux/Slices/Reservation/Block/Reservationblockslice ";
import { useNavigate } from "react-router-dom";
// ^ Adjust the import path above to match where you place ReservationBlockSlice.js

const Symbol = ({ children, className = "" }) => (
  <span
    className={`material-symbols-outlined ${className}`}
    aria-hidden="true"
  >
    {children}
  </span>
);

const SummaryItem = ({ label, value }) => (
  <div className="rg-summary-item">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="rg-field">
    <span className="rg-field-label">
      {required && <em>*</em>}
      {label}
    </span>
    {children}
  </label>
);

const formatDate = (value) => {
  const [year, month, day] = value.split("-");
  return `${day}-${month}-${year}`;
};

const DateField = ({ value, onChange }) => {
  const pickerRef = useRef(null);

  const openPicker = () => {
    if (pickerRef.current?.showPicker) pickerRef.current.showPicker();
    else pickerRef.current?.click();
  };

  return (
    <div className="rg-input-wrap">
      <input
        type="text"
        value={formatDate(value)}
        onClick={openPicker}
        aria-label="Date"
        readOnly
      />
      <input
        ref={pickerRef}
        type="date"
        className="rg-native-date-picker"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        aria-hidden="true"
      />
      <button
        type="button"
        className="rg-date-button"
        onClick={openPicker}
        aria-label="Choose date"
      >
        <Symbol>calendar_month</Symbol>
      </button>
    </div>
  );
};

const VISIBLE_ROOM_COUNT = 8;

const createGridValues = (roomCount) => ({
  original: Array(roomCount).fill("0"),
  occupancy1: Array(roomCount).fill("0"),
  occupancy2: Array(roomCount).fill("0"),
  occupancy3: Array(roomCount).fill("0"),
  occupancy4: Array(roomCount).fill("0"),
  rates: Array(roomCount).fill("0"),
  adult1: Array(roomCount).fill("0"),
  adult2: Array(roomCount).fill("0"),
  adult3: Array(roomCount).fill("0"),
  adult4: Array(roomCount).fill("0"),
  extraAdult: Array(roomCount).fill("0"),
});

const roomDetailRows = [
  ["occupancy1", "Occupancy 1"],
  ["occupancy2", "Occupancy 2"],
  ["occupancy3", "Occupancy 3"],
  ["occupancy4", "Occupancy 4"],
];
const occupancyKeys = roomDetailRows.map(([key]) => key);

const rateDetailRows = [
  ["adult1", "Adult 1"],
  ["adult2", "Adult 2"],
  ["adult3", "Adult 3"],
  ["adult4", "Adult 4"],
  ["extraAdult", "Extra Adult"],
];
const rateDetailKeys = rateDetailRows.map(([key]) => key);

export const RoomGrid = () => {
  const { selectedProperty } = useProperty();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { gridLoading } = useSelector((state) => state.reservationBlock);

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roomWindowStart, setRoomWindowStart] = useState(0);
  const [slideDirection, setSlideDirection] = useState("right");
  const [showBusinessCard, setShowBusinessCard] = useState(true);
  const [groupBy, setGroupBy] = useState("date");
  const [editMode, setEditMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState("2026-09-01");
  const [currency] = useState("INR");
  const [expandedRows, setExpandedRows] = useState({
    rooms: false,
    rates: false,
  });
  const [savedGridValues, setSavedGridValues] = useState(() => createGridValues(0));
  const [gridValues, setGridValues] = useState(() => createGridValues(0));

  const roomTypes = rooms.map((room) => room.RoomCode).filter(Boolean);
  const visibleRoomIndexes = roomTypes
    .map((_, index) => index)
    .slice(roomWindowStart, roomWindowStart + VISIBLE_ROOM_COUNT);
  const visibleColumnCount = Math.max(visibleRoomIndexes.length, 1);
  const canSlideLeft = roomWindowStart > 0;
  const canSlideRight = roomWindowStart + VISIBLE_ROOM_COUNT < roomTypes.length;

  const toNumber = (value) => {
    const number = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(number) ? number : 0;
  };

  const getRowTotal = (row) =>
    gridValues[row].reduce((total, value) => total + toNumber(value), 0);

  const originalRoomTotals = roomTypes.map((_, index) =>
    occupancyKeys.reduce(
      (total, row) => total + toNumber(gridValues[row][index]),
      0,
    ),
  );

  const originalRoomsGrandTotal = originalRoomTotals.reduce(
    (total, value) => total + value,
    0,
  );

  const roomRateTotals = roomTypes.map((_, index) =>
    rateDetailKeys.reduce(
      (total, row) => total + toNumber(gridValues[row][index]),
      0,
    ),
  );

  const formatAmount = (value) =>
    value === 0 ? "0" : value.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const toggleRow = (row) => {
    setExpandedRows((current) => ({
      ...current,
      [row]: !current[row],
    }));
  };

  const updateGridValue = (row, index, value) => {
    setGridValues((current) => ({
      ...current,
      [row]: current[row].map((cell, cellIndex) =>
        cellIndex === index ? value : cell
      ),
    }));
  };

  const copyGridValues = (values) =>
    Object.fromEntries(
      Object.entries(values).map(([key, cells]) => [key, [...cells]])
    );

  const startEditing = () => {
    setGridValues(copyGridValues(savedGridValues));
    setEditMode(true);
  };

  const cancelEditing = () => {
    setGridValues(copyGridValues(savedGridValues));
    setEditMode(false);
  };

  // const submitChanges = async () => {
  //   const roomGridPayload = roomTypes.map((roomType, index) => ({
  //     RoomType: roomType,
  //     OriginalRooms: originalRoomTotals[index] || 0,
  //     Occupancy1: toNumber(gridValues.occupancy1[index]),
  //     Occupancy2: toNumber(gridValues.occupancy2[index]),
  //     Occupancy3: toNumber(gridValues.occupancy3[index]),
  //     Occupancy4: toNumber(gridValues.occupancy4[index]),
  //     Rate: toNumber(gridValues.adult1[index]),
  //     Adult1: toNumber(gridValues.adult1[index]),
  //     Adult2: toNumber(gridValues.adult2[index]),
  //     Adult3: toNumber(gridValues.adult3[index]),
  //     Adult4: toNumber(gridValues.adult4[index]),
  //     ExtraAdult: toNumber(gridValues.extraAdult[index]),
  //   }));

  //   const postData = {
  //     PropertyID: selectedProperty?.PropertyID,
  //     Date: selectedDate,
  //     Currency: currency,
  //     RoomGrid: roomGridPayload,
  //   };

  //   try {
  //     const response = await dispatch(saveBlockRoomGrid(postData)).unwrap();
  //     // API responds with an array: [{ Status, Message, GridID }]
  //     const result = Array.isArray(response) ? response[0] : response;
  //     const isSuccess = result?.Status === 1;

  //     swal({
  //       title: isSuccess ? "Success" : "Error",
  //       text: result?.Message || "Something went wrong.",
  //       icon: isSuccess ? "success" : "error",
  //     }).then(() => {
  //       if (isSuccess) {
  //         setSavedGridValues(copyGridValues(gridValues));
  //         setEditMode(false);
  //         dispatch(resetBlockRoomGridState());
  //       }
  //     });
  //   } catch (error) {
  //     swal({
  //       title: "Error",
  //       text: typeof error === "string" ? error : "Something went wrong while saving the room grid.",
  //       icon: "error",
  //     });
  //   }
  // };

  const submitChanges = async () => {
  const roomGridPayload = roomTypes.map((roomType, index) => ({
    RoomType: roomType,
    OriginalRooms: originalRoomTotals[index] || 0,
    Occupancy1: toNumber(gridValues.occupancy1[index]),
    Occupancy2: toNumber(gridValues.occupancy2[index]),
    Occupancy3: toNumber(gridValues.occupancy3[index]),
    Occupancy4: toNumber(gridValues.occupancy4[index]),
    Rate: toNumber(gridValues.adult1[index]),
    Adult1: toNumber(gridValues.adult1[index]),
    Adult2: toNumber(gridValues.adult2[index]),
    Adult3: toNumber(gridValues.adult3[index]),
    Adult4: toNumber(gridValues.adult4[index]),
    ExtraAdult: toNumber(gridValues.extraAdult[index]),
  }));

  const postData = {
    PropertyID: selectedProperty?.PropertyID,
    Date: selectedDate,
    Currency: currency,
    RoomGrid: roomGridPayload,
  };

  try {
    const response = await dispatch(saveBlockRoomGrid(postData)).unwrap();

    const result = Array.isArray(response) ? response[0] : response;
    const isSuccess = result?.Status === 1;

    swal({
      title: isSuccess ? "Success" : "Error",
      text: result?.Message || "Something went wrong.",
      icon: isSuccess ? "success" : "error",
    }).then(() => {
      if (isSuccess) {
        setSavedGridValues(copyGridValues(gridValues));
        setEditMode(false);
        dispatch(resetBlockRoomGridState());

        // Success ke baad Manage Block page par
        navigate("/Reservation/ManageBlock");
      }
    });
  } catch (error) {
    swal({
      title: "Error",
      text:
        typeof error === "string"
          ? error
          : "Something went wrong while saving the room grid.",
      icon: "error",
    });
  }
};

  const slideRooms = (direction) => {
    if (direction === "left" && canSlideLeft) {
      setSlideDirection("left");
      setRoomWindowStart((current) => current - 1);
    }
    if (direction === "right" && canSlideRight) {
      setSlideDirection("right");
      setRoomWindowStart((current) => current + 1);
    }
  };

  const renderGridInput = (
    row,
    value,
    index,
    accent = false,
    locked = false
  ) => {
    const isReadOnly = locked || !editMode;

    return (
      <input
        className={`rg-grid-input rg-slide-cell rg-slide-${slideDirection} ${accent ? "is-accent" : ""}`}
        type="text"
        value={value}
        readOnly={isReadOnly}
        aria-readonly={isReadOnly}
        onChange={(event) => updateGridValue(row, index, event.target.value)}
      />
    );
  };


  // ─── Fetch Rooms ──────────────────────────────────────────
  useEffect(() => {
    let isActive = true;

    const fetchRoomData = async () => {
      if (!selectedProperty?.PropertyID) {
        setRooms([]);
        return;
      }

      setLoading(true);
      try {
        const data = await ResRoomlist(selectedProperty.PropertyID);
        if (!isActive) return;

        const roomList = Array.isArray(data?.RoomsDetails)
          ? data.RoomsDetails.filter((room) => room?.RoomCode)
          : [];
        const nextGridValues = createGridValues(roomList.length);

        setRooms(roomList);
        setRoomWindowStart(0);
        setGridValues(nextGridValues);
        setSavedGridValues(copyGridValues(nextGridValues));
      } catch (error) {
        if (isActive) {
          setRooms([]);
          setRoomWindowStart(0);
          console.error("Room data fetch error:", error);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchRoomData();
    return () => {
      isActive = false;
    };
  }, [selectedProperty?.PropertyID]);

  return (
    <>
    {(loading || gridLoading) && (<FullScreenLoader />)}
    <div className="wrapper">
      <ReservationSidebar />
      <div className="main-panel">
        <ReservationHeader />

        <div className="container">
          <div className="page-inner room-grid-page">
            <main className="room-grid-shell">
              {showBusinessCard && (
                <div className="rg-card rg-business-card">
                  <div className="rg-business-top">
                    <div className="rg-block-identity">
                      <button type="button" className="rg-title-link">
                        Classic Test
                      </button>
                      <div className="rg-reference">
                        <span>593275</span>
                        <span>090126CLA</span>
                      </div>
                    </div>

                    <div className="rg-block-dates">
                      <span className="rg-overline">Block Dates</span>
                      <div className="rg-date-range">
                        <span className="rg-date-chip">
                          <b>01-09-2026</b>
                          <small>Tuc</small>
                        </span>
                        <span className="rg-range-divider">- 1 -</span>
                        <span className="rg-date-chip">
                          <b>02-09-2026</b>
                          <small>Wed</small>
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="rg-collapse"
                      aria-label="Collapse business card"
                      onClick={() => setShowBusinessCard(false)}
                    >
                      <Symbol>remove</Symbol>
                    </button>
                  </div>

                  <div className="rg-summary-grid">
                    <SummaryItem label="Block Status" value="TEN" />
                    <SummaryItem label="Block Owner" value="ALL" />
                    <SummaryItem label="Room Owner" value="ALL" />
                    <SummaryItem label="Catering Owner" value="ALL" />
                    <SummaryItem label="Property" value="50512" />
                  </div>

                  <button
                    type="button"
                    className="rg-hide-card"
                    onClick={() => setShowBusinessCard(false)}
                  >
                    <span />
                    Hide Business Card
                  </button>
                </div>
              )}

              {!showBusinessCard && (
                <button
                  type="button"
                  className="rg-show-card"
                  onClick={() => setShowBusinessCard(true)}
                >
                  Show Business Card
                </button>
              )}

              <div className="rg-card rg-search-card">
                <div className="rg-filter-grid">
                  <Field label="Date" required>
                    <DateField value={selectedDate} onChange={setSelectedDate} />
                  </Field>

                  <Field label="Range">
                    <div className="rg-input-wrap">
                      <select defaultValue="7 Days" aria-label="Range">
                        <option>7 Days</option>
                        <option>14 Days</option>
                        <option>21 Days</option>
                        <option>30 Days</option>
                      </select>
                      <Symbol>expand_more</Symbol>
                    </div>
                  </Field>

                  <Field label="Room Types">
                    <div className="rg-room-type-control">
                      <div className="rg-input-wrap">
                        <input
                          type="text"
                          placeholder="Select room types..."
                          aria-label="Room types"
                        />
                        <Symbol>search</Symbol>
                      </div>
                      <button type="button" aria-label="Room type information">
                        <Symbol>info</Symbol>
                      </button>
                    </div>
                  </Field>
                </div>

                <div className="rg-search-footer">
                  <div className="rg-group-by">
                    <span className="rg-overline">Group By</span>
                    <label>
                      <input
                        type="radio"
                        name="groupBy"
                        checked={groupBy === "grid"}
                        onChange={() => setGroupBy("grid")}
                      />
                      <span />
                      Grid Type
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="groupBy"
                        checked={groupBy === "date"}
                        onChange={() => setGroupBy("date")}
                      />
                      <span />
                      Date
                    </label>
                  </div>

                  <div className="rg-search-actions">                   
                    <button type="button" className="rg-search-button">
                      Search
                    </button>
                  </div>
                </div>
              </div>

              <div className="rg-card rg-grid-card">
                <div className="rg-grid-toolbar">
                  <div className="rg-currency">
                    <span>Currency</span>
                    <strong>{currency}</strong>
                  </div>

                   {!editMode && (
                      <button type="button" className="fs-6 rg-expand-all" onClick={startEditing}>
                        EDIT
                      </button>
                    )}
                </div>

                <div className="rg-table-scroll">
                  <div
                    className="rg-table"
                    style={{ "--rg-room-columns": visibleColumnCount }}
                  >
                    <div className="rg-room-heading-row">
                      <button
                        type="button"
                        className="rg-table-arrow"
                        onClick={() => slideRooms("left")}
                        disabled={!canSlideLeft}
                        aria-label="Show previous room type"
                      >
                        <Symbol>chevron_left</Symbol>
                      </button>
                      <span className="rg-heading-spacer" aria-hidden="true" />
                      {visibleRoomIndexes.map((index) => (
                        <span
                          className={`rg-slide-cell rg-slide-${slideDirection}`}
                          key={`${roomWindowStart}-${rooms[index].RoomID ?? index}`}
                          title={rooms[index].RoomName}
                        >
                          {rooms[index].RoomCode}
                        </span>
                      ))}
                      {roomTypes.length === 0 && (
                        <span className="rg-empty-rooms">No room types found</span>
                      )}
                      <button
                        type="button"
                        className="rg-table-arrow rg-table-arrow-right"
                        onClick={() => slideRooms("right")}
                        disabled={!canSlideRight}
                        aria-label="Show next room type"
                      >
                        <Symbol>chevron_right</Symbol>
                      </button>
                    </div>

                    <div className="rg-day-heading">
                      <strong>{formatDate(selectedDate)}</strong>
                      <em>Tuesday</em>
                    </div>

                    <>
                        <div className="rg-data-row">
                          <div className="rg-row-label">
                            <button
                              type="button"
                              className={expandedRows.rooms ? "is-expanded" : ""}
                              aria-label={
                                expandedRows.rooms
                                  ? "Collapse original rooms"
                                  : "Expand original rooms"
                              }
                              aria-expanded={expandedRows.rooms}
                              onClick={() => toggleRow("rooms")}
                            >
                              <Symbol>
                                {expandedRows.rooms ? "remove" : "add"}
                              </Symbol>
                            </button>
                            <span>Original Rooms</span>
                            <strong>{originalRoomsGrandTotal}</strong>
                          </div>
                          {visibleRoomIndexes.map((index) => (
                            <React.Fragment key={`original-${roomWindowStart}-${rooms[index].RoomID ?? index}`}>
                              {renderGridInput(
                                "original",
                                originalRoomTotals[index],
                                index,
                                false,
                                true
                              )}
                            </React.Fragment>
                          ))}
                        </div>

                        <div
                          className={`rg-expandable ${
                            expandedRows.rooms ? "is-open" : ""
                          }`}
                        >
                          <div className="rg-expandable-inner">
                            {roomDetailRows.map(([key, label]) => (
                              <div className="rg-detail-row" key={key}>
                                <div className="rg-detail-label">
                                  <span>{label}</span>
                                  <strong>{getRowTotal(key)}</strong>
                                </div>
                                {visibleRoomIndexes.map((index) => (
                                  <React.Fragment key={`${key}-${roomWindowStart}-${rooms[index].RoomID ?? index}`}>
                                    {renderGridInput(
                                      key,
                                      gridValues[key][index],
                                      index
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rg-data-row">
                          <div className="rg-row-label">
                            <button
                              type="button"
                              className={expandedRows.rates ? "is-expanded" : ""}
                              aria-label={
                                expandedRows.rates
                                  ? "Collapse rooms"
                                  : "Expand rooms"
                              }
                              aria-expanded={expandedRows.rates}
                              onClick={() => toggleRow("rates")}
                            >
                              <Symbol>
                                {expandedRows.rates ? "remove" : "add"}
                              </Symbol>
                            </button>
                            <span>Rooms</span>
                          </div>
                          {visibleRoomIndexes.map((index) => (
                            <React.Fragment key={`rate-${roomWindowStart}-${rooms[index].RoomID ?? index}`}>
                              {renderGridInput(
                                "rates",
                                formatAmount(roomRateTotals[index]),
                                index,
                                index === roomTypes.length - 1,
                                true
                              )}
                            </React.Fragment>
                          ))}
                        </div>

                        <div
                          className={`rg-expandable ${
                            expandedRows.rates ? "is-open" : ""
                          }`}
                        >
                          <div className="rg-expandable-inner">
                            {rateDetailRows.map(([key, label]) => (
                              <div className="rg-detail-row" key={key}>
                                <div className="rg-detail-label">
                                  <span>{label}</span>
                                </div>
                                {visibleRoomIndexes.map((index) => (
                                  <React.Fragment key={`${key}-${roomWindowStart}-${rooms[index].RoomID ?? index}`}>
                                    {renderGridInput(
                                      key,
                                      gridValues[key][index],
                                      index,
                                      index === roomTypes.length - 1
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                    </>
                  </div>
                </div>

                {editMode && (
                  <div className="rg-edit-footer">
                    <button
                      type="button"
                      className="rg-cancel-button"
                      onClick={cancelEditing}
                      disabled={gridLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rg-submit-button"
                      onClick={submitChanges}
                      disabled={gridLoading}
                    >
                      {gridLoading ? "Saving..." : "Submit"}
                    </button>
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default RoomGrid;